import { ChalkEmployeeGuard } from "@/components/admin/ChalkEmployeeGuard";
import { useAgentWorkspaceControls } from "@/components/agent-conversation/AgentSidebarContext";
import { ChalkAssistantRail } from "@/components/agent-conversation/ChalkAssistantRail";
import { DynamicCommandPaletteContainer } from "@/components/command-palette/DynamicCommandPalette";
import {
  ChalkLayoutErrorComponent,
  ChalkLayoutErrorComponentWithoutBack,
} from "@/components/layout/ErrorBoundary";
import { LayoutWarningBanners } from "@/components/layout/LayoutWarningBanners";
import {
  ProjectSecondarySidebarToggleV2,
  ProjectSecondarySidebarV2,
  useProjectSecondarySidebarPinnedFor,
} from "@/components/navigation/ProjectCategoryNavV2";
import {
  ProjectNavbar,
  useProjectNavbarUsesCategoryRail,
} from "@/components/navigation/ProjectNavbar";
import { ProjectNavbarProps } from "@/components/navigation/ProjectNavbar.types";
import { Skeleton } from "@/components/loaders/Skeleton";
import { useCategoryNavSettled } from "@/utils/hooks/use-category-nav-enabled";
import { ProjectTopRailV2 } from "@/components/navigation/ProjectTopRailV2";
import {
  TeamNavbar,
  TeamNavbarProps,
  useTeamNavbarUsesCategoryRail,
} from "@/components/navigation/TeamNavbar";
import { TeamTopRailV2 } from "@/components/navigation/TeamTopRailV2";
import { NavLinkEnvironmentProvider } from "@/components/navigation/nav-link-environment";
import {
  CategoryRailCollapseProvider,
  CategoryRailTopRailInset,
} from "@/components/navigation/category-rail-collapse";
import { EnvironmentNotFoundError } from "@/components/networking/EnvironmentNotFoundError";
import { SuspendedEnvironmentWarning } from "@/components/networking/SuspendedEnvironmentWarning";
import { ZIndexClass } from "@/constants/z-index";
import { classNames, cx } from "@/utils/classnames";
import { useRumIdentity } from "@/utils/datadog-init";
import {
  useAmplitudeBaseIdentify,
  useAmplitudePageview,
} from "@/utils/hooks/use-amplitude-pageview";
import { useAutoLogout } from "@/utils/hooks/use-auto-logout";
import { useAutoSessionRefresh } from "@/utils/hooks/use-auto-session-refresh";
import { useEnvironmentTitle } from "@/utils/hooks/use-env-title";
import { useFaviconPath } from "@/utils/hooks/use-favicon-path";
import { useSession } from "next-auth/react";
import { ErrorBoundary } from "next/dist/client/components/error-boundary";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { PropsWithChildren, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { AmplitudeEvent, AmplitudeEventPropertiesProvider, AmplitudeScript } from "utils/amplitude";
import { Girdle } from "./Girdle";

export interface LayoutV2Props extends PropsWithChildren {
  allowNarrowWidth?: boolean;
  /**
   * Element rendered underneath the top rail. E.g. "branch" label or "deployment" label on deployment and branch deployment pages, respectively.
   */
  absoluteLabel?: ReactNode;
  containerClassName?: string;
  fullWidthLayout?: boolean;
  loggedIn?: boolean;
  /**
   * Extra classes for the main scroll/content container. Use sparingly for pages
   * with canvas-like layouts that need to opt out of default document padding.
   */
  mainContentClassName?: string;
  /**
   * Extra classes for the content wrapper inside the main scroll container. This is useful when a
   * page needs its own internal layout to fill the post-banner content pane height.
   */
  mainContentWrapperClassName?: string;
  /**
   * Let the browser document own vertical scrolling instead of the layout's inner
   * content pane. This is for full-page fixtures whose scrollbar should sit at
   * the viewport edge.
   */
  useDocumentScroll?: boolean;
  /**
   * Most document-like pages need bottom breathing room. Full-height canvas/table
   * pages own their viewport height and should opt out to avoid dead scroll space.
   */
  mainContentHasBottomPadding?: boolean;
  /**
   * Left-side navbar. Examples:
   * - project navbar (environment-scoped pages). Most common. E.g. /projects/<p>/environments/<e>/*
   * - team navbar (team-scoped pages). Only used on a few pages. E.g. /projects
   * - deployment navbar (deployment-scoped pages). Pages scoped to a particular deployment. E.g. /projects/<p>/environments/<e>/deployments/<d>/*
   * - branch-deployment navbar (branch deployment-scoped pages). Pages scoped to a particular branch deployment. E.g. /projects/<p>/environments/<e>/branches/<b>/deployments/<d>/*
   */
  navBar?: ReactNode;
  /** Hide the left-side navigation while preserving the top rail and V2 content shell. */
  hideSideNav?: boolean;
  /** Visually defocus and disable the top rail while page content owns interaction. */
  defocusTopRail?: boolean;
  noNav?: boolean;
  /**
   * Second layer left-side navbar, e.g. for settings panes.
   */
  secondaryNavbar?: ReactNode;
  title?: string;
  /**
   * Element above main content, e.g. offline query details page progress bar.
   */
  topAccessory?: ReactNode;
}

// Drawer-hosted pages keep this inset inside their own scrollport so it scrolls away.
export const LAYOUT_TOP_PADDING_CLASSNAME =
  "pt-cds-12 [--layout-content-top-padding:12px] [&:has(.detail-page-run-drawer)]:pt-0";
export const LAYOUT_X_PADDING_CLASSNAME = "px-page-text";
export const LAYOUT_BOTTOM_PADDING_CLASSNAME = "pb-16";
const LAYOUT_TITLE_CLASSNAME = "[&_h1]:!text-cds-xl [&_h1]:cursor-default [&_h1]:self-start";
const CATEGORY_NAV_LAYOUT_TITLE_CLASSNAME = `${LAYOUT_TITLE_CLASSNAME} [&_h1]:!font-medium`;
const NAVIGATION_V2_SIDEBAR_SWAP_ANIMATION_MS = 150;

type NavigationV2NavKind = "project" | "team";

let previousClientNavigationV2NavKind: NavigationV2NavKind | undefined;

/**
 * The project top rail, led by the category nav's sidebar toggle while the sidebar is hidden. Pinned
 * open, the sidebar's title row carries the toggle, so the rail's slot for it closes up, with the
 * same 200ms ease-out as the sidebar's own width.
 */
const ProjectTopRailWithSidebarToggle: React.FC<{ navProps: ProjectNavbarProps }> = ({
  navProps,
}) => {
  const sidebarPinned = useProjectSecondarySidebarPinnedFor(navProps);
  return (
    <ProjectTopRailV2
      leadingAccessory={<ProjectSecondarySidebarToggleV2 {...navProps} />}
      leadingAccessoryCollapsed={sidebarPinned}
    />
  );
};

export const LayoutV2: React.FC<LayoutV2Props> = ({
  allowNarrowWidth,
  loggedIn,
  navBar,
  hideSideNav = false,
  defocusTopRail = false,
  fullWidthLayout,
  secondaryNavbar,
  children,
  title = "",
  noNav,
  containerClassName,
  mainContentClassName,
  mainContentWrapperClassName,
  mainContentHasBottomPadding = true,
  useDocumentScroll = false,
  absoluteLabel,
  topAccessory,
}) => {
  useAmplitudePageview(AmplitudeEvent.VIEW_PAGE);
  useAmplitudeBaseIdentify();
  useAutoLogout();
  useAutoSessionRefresh();
  useRumIdentity();
  const session = useSession();
  const router = useRouter();
  const mainContentRef = useRef<HTMLElement | null>(null);
  const navBarIsProjectNavbar = React.isValidElement(navBar) && navBar.type === ProjectNavbar;
  const navBarIsTeamNavbar = React.isValidElement(navBar) && navBar.type === TeamNavbar;
  const teamNavbarProps = navBarIsTeamNavbar
    ? (navBar as React.ReactElement<TeamNavbarProps>).props
    : undefined;
  const navBarProps = React.isValidElement(navBar)
    ? (navBar.props as {
        activePage?: string;
        activeSubPage?: string;
        activePageV2?: string;
        activeSubPageV2?: string;
      })
    : undefined;
  const activePage = navBarProps?.activePageV2 ?? navBarProps?.activePage;
  const activeSubPage = navBarProps?.activeSubPageV2 ?? navBarProps?.activeSubPage;
  const navigationV2Available =
    loggedIn && navBar != null && (navBarIsProjectNavbar || navBarIsTeamNavbar);
  const navigationV2Enabled = navigationV2Available && !noNav;
  const navigationV2FullPageRailEnabled = navigationV2Available && noNav && navBarIsProjectNavbar;
  const navigationV2ShellEnabled = navigationV2Enabled || navigationV2FullPageRailEnabled;
  // The category rail's second level replaces any page-level secondary navbar (Settings), which
  // becomes the Settings category's sidebar.
  const navBarUsesCategoryRail = useProjectNavbarUsesCategoryRail(
    navBarIsProjectNavbar ? (navBar as React.ReactElement<ProjectNavbarProps>).props : {},
  );
  const projectCategoryNavProps: ProjectNavbarProps | undefined =
    navigationV2ShellEnabled && navBarIsProjectNavbar && navBarUsesCategoryRail
      ? {
          ...(navBar as React.ReactElement<ProjectNavbarProps>).props,
          defaultCollapsed:
            navigationV2FullPageRailEnabled ||
            (navBar as React.ReactElement<ProjectNavbarProps>).props.defaultCollapsed,
        }
      : undefined;
  // Under the category nav, a global settings page renders the rail too, with the global settings
  // sidebar as its second level. Its nav points into the environment the reader was last in.
  const {
    environment: globalNavEnvironment,
    usesCategoryRail: teamNavbarUsesCategoryRail,
    ready: teamNavbarChoiceReady,
  } = useTeamNavbarUsesCategoryRail();
  const globalCategoryNavProps: ProjectNavbarProps | undefined =
    navigationV2ShellEnabled && navBarIsTeamNavbar && teamNavbarUsesCategoryRail
      ? { globalSettings: { activePage: teamNavbarProps?.activePage } }
      : undefined;
  // The category nav's chrome (48px top rail, sidebar-matched page titles) applies only where the
  // page renders the category rail; classic team pages and override sidebars keep the classic
  // chrome.
  const categoryNavShell = projectCategoryNavProps != null || globalCategoryNavProps != null;
  const projectNavbarChoiceReady = useCategoryNavSettled();
  // Until the flag that picks the navigation has loaded, the top rail holds a placeholder, as the
  // navbars do (ProjectNavbar, TeamNavbar), so one navigation's chrome never flashes before the
  // other's. A page that supplies its own sidebar keeps the classic one either way.
  const navigationChoicePending =
    navigationV2ShellEnabled &&
    (navBarIsTeamNavbar
      ? !teamNavbarChoiceReady
      : navBarIsProjectNavbar &&
        (navBar as React.ReactElement<ProjectNavbarProps>).props.sideNavOverride == null &&
        !projectNavbarChoiceReady);
  const assistantWorkspace = useAgentWorkspaceControls();
  // Route templates are available before hydration supplies the environment ids.
  // Reserve the rail while its eligibility resolves, without enabling assistant actions.
  const assistantRailLoading =
    navigationV2ShellEnabled &&
    assistantWorkspace?.available !== true &&
    (assistantWorkspace != null || router.pathname?.includes("[environmentId]")) &&
    (!router.isReady || session.status === "loading");
  const hasAssistantRail =
    navigationV2ShellEnabled && (assistantWorkspace?.available === true || assistantRailLoading);
  // A global settings page on the category rail keeps the same rail, so moving there and back is no
  // sidebar swap.
  const navigationV2NavKind: NavigationV2NavKind | undefined = navigationV2ShellEnabled
    ? navBarIsTeamNavbar && globalCategoryNavProps == null
      ? "team"
      : "project"
    : undefined;
  const initialNavigationV2SidebarSwapKind = useMemo(() => {
    if (typeof window === "undefined" || navigationV2NavKind == null) return undefined;

    return previousClientNavigationV2NavKind != null &&
      previousClientNavigationV2NavKind !== navigationV2NavKind
      ? navigationV2NavKind
      : undefined;
  }, [navigationV2NavKind]);
  const [navigationV2SidebarSwapKind, setNavigationV2SidebarSwapKind] = useState<
    NavigationV2NavKind | undefined
  >(initialNavigationV2SidebarSwapKind);
  const navigationV2SidebarSwapAnimationClass =
    navigationV2SidebarSwapKind == null ? undefined : "animate-sidenav-fade-in";
  const layoutErrorComponent = navigationV2ShellEnabled
    ? ChalkLayoutErrorComponentWithoutBack
    : ChalkLayoutErrorComponent;
  const renderedNavBar =
    navigationV2FullPageRailEnabled && navBarIsProjectNavbar
      ? React.cloneElement(navBar as React.ReactElement<ProjectNavbarProps>, {
          defaultCollapsed: true,
        })
      : navBar;
  const shouldRenderNavBar =
    !hideSideNav && renderedNavBar != null && (!noNav || navigationV2FullPageRailEnabled);

  const environmentTitle = useEnvironmentTitle(title);

  const faviconPath = useFaviconPath();

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [router.pathname]);

  useEffect(() => {
    const previousNavKind = previousClientNavigationV2NavKind;

    if (navigationV2NavKind != null) {
      previousClientNavigationV2NavKind = navigationV2NavKind;
    }

    if (
      previousNavKind == null ||
      navigationV2NavKind == null ||
      previousNavKind === navigationV2NavKind
    ) {
      return;
    }

    setNavigationV2SidebarSwapKind(navigationV2NavKind);
    const timeoutId = window.setTimeout(() => {
      setNavigationV2SidebarSwapKind(undefined);
    }, NAVIGATION_V2_SIDEBAR_SWAP_ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [navigationV2NavKind]);

  return (
    <AmplitudeEventPropertiesProvider
      properties={{ page: title, subpage: activeSubPage, active_page: activePage }}
    >
      {/* Global settings pages push the rail's environment icons off-screen. */}
      <CategoryRailCollapseProvider collapsed={globalCategoryNavProps != null}>
        {loggedIn && <DynamicCommandPaletteContainer />}
        <div
          className={classNames(
            "design-system-typography min-h-screen flex flex-col",
            hasAssistantRail && "[--assistant-bottom-rail-height:40px]",
            // The category nav's top rail is 48px; TOP_RAIL_HEIGHT in tailwind.config.js reads this.
            categoryNavShell && "[--top-rail-height:48px]",
            navigationV2ShellEnabled ? "bg-cds-surface-active dark:bg-cds-inset-dark" : "bg-white",
            !allowNarrowWidth && "min-w-[960px]",
            // The V2 shell normally clamps to the viewport and clips overflow so
            // inner panes own their own scroll. Pages that opt into document
            // scrolling (useDocumentScroll, e.g. the notebook editor) must NOT be
            // clamped here, or the outer overflow-hidden swallows page scroll even
            // though the inner containers allow it.
            navigationV2ShellEnabled && !useDocumentScroll && "h-screen min-h-0 overflow-hidden",
          )}
        >
          <Head>
            <title>{environmentTitle}</title>
            <meta charSet="utf-8" />
            <meta name="viewport" content="initial-scale=1.0, width=device-width" />
            <link rel="shortcut icon" href={faviconPath} />
            <AmplitudeScript />
          </Head>

          <div
            className={classNames(
              "flex flex-row flex-grow text-cds-md",
              navigationV2ShellEnabled &&
                (useDocumentScroll
                  ? "min-h-screen bg-cds-surface-active dark:bg-cds-inset-dark"
                  : "h-screen min-h-0 overflow-hidden bg-cds-surface-active dark:bg-cds-inset-dark"),
              containerClassName,
            )}
          >
            {shouldRenderNavBar && (
              <div
                className={classNames(
                  navigationV2ShellEnabled
                    ? [
                        "relative overflow-visible transform-gpu motion-reduce:animate-none",
                        ZIndexClass.NAVIGATION_CHROME,
                        navigationV2SidebarSwapAnimationClass,
                      ]
                    : "bg-white",
                )}
              >
                {renderedNavBar}
              </div>
            )}
            {secondaryNavbar && !navigationV2ShellEnabled && (
              <div className="bg-white">{secondaryNavbar}</div>
            )}
            <div
              className={classNames(
                "flex-1 flex flex-col min-w-0",
                navigationV2ShellEnabled && !useDocumentScroll
                  ? "h-screen min-h-0 overflow-hidden"
                  : "min-h-screen",
              )}
            >
              {navigationV2ShellEnabled ? (
                <>
                  <div
                    className={classNames(
                      "h-top-rail shrink-0 overflow-hidden transition-[filter,opacity]",
                      defocusTopRail && "pointer-events-none select-none opacity-45 blur-sm",
                    )}
                    inert={defocusTopRail || undefined}
                    aria-hidden={defocusTopRail || undefined}
                  >
                    <CategoryRailTopRailInset>
                      {navigationChoicePending ? (
                        <div
                          aria-busy={true}
                          aria-label={"Loading navigation"}
                          className={"flex h-full items-center pl-cds-16"}
                        >
                          <Skeleton className={"w-48"} shape={"text"} tone={"subtle"} />
                        </div>
                      ) : navBarIsTeamNavbar ? (
                        // Scoped to the environment the rail points into, so the top rail takes
                        // the category nav's form whenever the rail does.
                        <NavLinkEnvironmentProvider environment={globalNavEnvironment}>
                          <TeamTopRailV2
                            activePage={teamNavbarProps?.activePage}
                            leadingAccessory={
                              globalCategoryNavProps != null ? (
                                <ProjectSecondarySidebarToggleV2 {...globalCategoryNavProps} />
                              ) : undefined
                            }
                          />
                        </NavLinkEnvironmentProvider>
                      ) : projectCategoryNavProps != null ? (
                        <ProjectTopRailWithSidebarToggle navProps={projectCategoryNavProps} />
                      ) : (
                        <ProjectTopRailV2 />
                      )}
                    </CategoryRailTopRailInset>
                  </div>
                  {absoluteLabel}
                  {topAccessory}
                  <ChalkEmployeeGuard>
                    <LayoutWarningBanners loggedIn={loggedIn} />
                  </ChalkEmployeeGuard>
                  {loggedIn && <EnvironmentNotFoundError />}
                  {loggedIn && <SuspendedEnvironmentWarning />}
                  {/* Content flexes within the space left by the non-shrinking assistant rail. */}
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div
                      className={cx(
                        "min-h-0 flex-1",
                        // overflow-clip, not overflow-hidden: overflow-hidden would make this a
                        // scroll container, and scrolling content into view -- a selected notebook
                        // cell, a deep link, an editor revealing a line -- scrolls every scrollable
                        // ancestor. Should the pane ever overflow this box, that would move it,
                        // tucking its top stroke under the top rail.
                        useDocumentScroll ? "overflow-visible" : "overflow-clip",
                      )}
                    >
                      <div
                        className={classNames(
                          // Stroked on every side but the right, with matching left corners. The
                          // pane fits its box exactly, so no stroke is clipped; `*-below-top-rail`
                          // leaves out the top and bottom strokes, so a child sized by it fits the
                          // content box exactly too.
                          "flex min-w-0 border-y-[1px] border-l-[1px] rounded-l-cds-6",
                          useDocumentScroll
                            ? // Content at least as tall as the viewport allows, strokes outside it.
                              "box-content min-h-below-top-rail overflow-visible"
                            : "h-full overflow-hidden",
                          "border-cds-surface-border bg-cds-surface-default",
                          "dark:border-white/[0.06] dark:bg-cds-elevated-dark",
                        )}
                      >
                        {projectCategoryNavProps != null ? (
                          <ProjectSecondarySidebarV2 {...projectCategoryNavProps} />
                        ) : globalCategoryNavProps != null ? (
                          <NavLinkEnvironmentProvider environment={globalNavEnvironment}>
                            <ProjectSecondarySidebarV2 {...globalCategoryNavProps} />
                          </NavLinkEnvironmentProvider>
                        ) : (
                          secondaryNavbar && <div className={"shrink-0"}>{secondaryNavbar}</div>
                        )}
                        <section
                          data-page-insets={!fullWidthLayout || undefined}
                          ref={mainContentRef}
                          style={
                            {
                              "--layout-content-inline-end-padding": fullWidthLayout
                                ? "0px"
                                : "var(--page-text-inset)",
                            } as React.CSSProperties
                          }
                          className={cx(
                            "relative min-w-0 flex-1",
                            useDocumentScroll ? "overflow-visible" : "overflow-auto",
                            categoryNavShell
                              ? CATEGORY_NAV_LAYOUT_TITLE_CLASSNAME
                              : LAYOUT_TITLE_CLASSNAME,
                            // Matches the top rail's height (TOP_RAIL_HEIGHT in tailwind.config.js).
                            categoryNavShell
                              ? "[--layout-v2-top-rail-visible-height:48px]"
                              : "[--layout-v2-top-rail-visible-height:40px]",
                            !fullWidthLayout &&
                              mainContentHasBottomPadding &&
                              LAYOUT_BOTTOM_PADDING_CLASSNAME,
                            !fullWidthLayout && LAYOUT_TOP_PADDING_CLASSNAME,
                            !fullWidthLayout && LAYOUT_X_PADDING_CLASSNAME,
                            mainContentClassName,
                          )}
                        >
                          <Girdle
                            disableMinWidth={allowNarrowWidth}
                            className={mainContentWrapperClassName}
                          >
                            <ErrorBoundary errorComponent={layoutErrorComponent}>
                              {children}
                            </ErrorBoundary>
                          </Girdle>
                        </section>
                      </div>
                    </div>
                    {assistantRailLoading ? (
                      <ChalkAssistantRail loading />
                    ) : hasAssistantRail ? (
                      <ChalkAssistantRail />
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {absoluteLabel}
                  {topAccessory}
                  <ChalkEmployeeGuard>
                    <LayoutWarningBanners loggedIn={loggedIn} />
                  </ChalkEmployeeGuard>
                  {loggedIn && <EnvironmentNotFoundError />}
                  {loggedIn && <SuspendedEnvironmentWarning />}
                  <div
                    data-page-insets={!fullWidthLayout || undefined}
                    style={
                      {
                        "--layout-content-inline-end-padding": fullWidthLayout
                          ? "0px"
                          : "var(--page-text-inset)",
                      } as React.CSSProperties
                    }
                    className={cx(
                      "relative flex-1",
                      LAYOUT_TITLE_CLASSNAME,
                      !fullWidthLayout &&
                        mainContentHasBottomPadding &&
                        LAYOUT_BOTTOM_PADDING_CLASSNAME,
                      !fullWidthLayout && LAYOUT_TOP_PADDING_CLASSNAME,
                      !fullWidthLayout && LAYOUT_X_PADDING_CLASSNAME,
                      mainContentClassName,
                    )}
                  >
                    <Girdle
                      disableMinWidth={allowNarrowWidth}
                      className={mainContentWrapperClassName}
                    >
                      <ErrorBoundary errorComponent={layoutErrorComponent}>
                        {children}
                      </ErrorBoundary>
                    </Girdle>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </CategoryRailCollapseProvider>
    </AmplitudeEventPropertiesProvider>
  );
};
